import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "../lib/content";

/**
 * Editorial bento-style gallery of real Reflex Stone Care projects.
 * Light theme, asymmetric grid.
 */
export default function ProjectsGallery() {
  return (
    <section data-testid="projects-gallery" className="relative py-24 md:py-32 bg-[#F7F4F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <div className="flex items-center gap-3 text-[#B8965A]">
              <span className="block w-8 h-px bg-[#C9A86A]" />
              <span className="text-[11px] uppercase tracking-[0.3em]">Recent Work</span>
            </div>
            <h2 className="mt-6 font-display-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#0A0A0A] max-w-3xl">
              Real Sydney stone, <span className="font-serif-italic font-light text-[#B8965A]">really restored.</span>
            </h2>
          </div>
          <p className="text-[#525252] max-w-md text-[15px] leading-relaxed">
            A small selection of recent residential and commercial projects across
            Sydney and the Central Coast.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-[180px] md:auto-rows-[220px]">
          {/* Marble lounge — large feature */}
          <Card project={PROJECTS[0]} className="col-span-12 md:col-span-7 row-span-2" delay={0} />
          {/* Travertine kitchen */}
          <Card project={PROJECTS[1]} className="col-span-6 md:col-span-5 row-span-1" delay={0.05} />
          {/* Marble bathroom */}
          <Card project={PROJECTS[2]} className="col-span-6 md:col-span-5 row-span-1" delay={0.1} />
          {/* Travertine ensuite */}
          <Card project={PROJECTS[3]} className="col-span-12 md:col-span-7 row-span-1" delay={0.15} />
          {/* Sandstone */}
          <Card project={PROJECTS[4]} className="col-span-12 md:col-span-5 row-span-1" delay={0.2} />
        </div>

        <div className="mt-12 flex justify-center md:justify-start">
          <Link
            to="/gallery"
            data-testid="projects-gallery-cta"
            className="group inline-flex items-center gap-3 px-8 py-4 border border-[#0A0A0A]/25 text-[#0A0A0A] uppercase tracking-[0.22em] text-[11px] hover:bg-[#0A0A0A] hover:text-white transition-colors"
          >
            View full gallery
            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function Card({ project, className = "", delay = 0 }) {
  return (
    <motion.figure
      data-testid={`project-${project.title.toLowerCase().replace(/\s+/g, "-")}`}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`relative group overflow-hidden bg-black ${className}`}
    >
      <img
        src={project.image}
        alt={`${project.title} — ${project.location}`}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500" />
      <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-8 flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#E8DAB8]">{project.stone}</div>
          <div className="mt-2 font-display-bold text-white text-lg md:text-2xl tracking-tight">
            {project.title}
          </div>
          <div className="mt-1 text-[12px] text-white/75">{project.location}</div>
        </div>
        <span className="hidden md:inline-flex items-center justify-center w-10 h-10 border border-white/40 text-white opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </figcaption>
    </motion.figure>
  );
}
